import { IconNames } from '@blueprintjs/icons';
import { CONTRACT_RATE, ReferentPriceTypes } from '@strata/constants';
import { DateFormats, formatDate, sortAsc } from '@strata/helper-functions';
import { DateInputFormik } from '@strata/react-date-time';
import { DialogWithForm } from '@strata/react-dialog';
import { BidInput, TextAreaFormik } from '@strata/react-inputs';
import { DetailValue } from '@strata/react-label-value-pairs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { validationStrings } from '@strata/strata-ui-strings';
import Decimal from 'decimal.js';
import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { useMemo } from 'react';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { useGet } from 'services/Http';
import { mapSuppliersToOptions } from 'services/NPISupport';
import {
    getPatientRelationshipDisplayText,
    isSamePatientPreference,
    PatientRelationshipType,
    PatientRelationshipTypeDisplayNameMap,
} from 'services/RouteRuleHelpers';

const OfferRateTypes = {
    MARKET: 'Market',
    CONTRACT: 'Contract',
    OVERRIDE: 'Override',
};

export const AddPatientRelationshipDialog = ({
    isOpen,
    onClose,
    patientRelationshipType,
    suppliers = undefined,
}) => {
    const patientPreferenceFormik = useFormikContext();
    const patientRelationshipTypeDisplayName =
        PatientRelationshipTypeDisplayNameMap[patientRelationshipType];
    const therapeuticCategoryList = useGet('/reference/therapeutic_categories/')?.sort(sortAsc);

    const supplierOptions = useMemo(() => mapSuppliersToOptions(suppliers), [suppliers]);

    return (
        <Formik
            initialValues={{ offerRateType: OfferRateTypes.MARKET, duplicateRelationships: [] }}
            enableReinitialize
            validationSchema={Yup.object({
                memberIds: Yup.string().required(validationStrings.requiredField.errorMessage),
                therapeutic_category: Yup.string(),
                supplier: Yup.object().required(validationStrings.requiredField.errorMessage),
                offerRateType: Yup.string()
                    .oneOf(Object.values(OfferRateTypes))
                    .required(validationStrings.requiredField.errorMessage),
                referentPriceType: Yup.string()
                    .oneOf(Object.values(ReferentPriceTypes))
                    .when('offerRateType', {
                        is: OfferRateTypes.OVERRIDE,
                        then: (schema) =>
                            schema.required(validationStrings.requiredField.errorMessage),
                    }),
                offerRate: BidInput.validator().when('offerRateType', {
                    is: OfferRateTypes.OVERRIDE,
                    then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
                }),
                end_date: DateInputFormik.validator(),
            })}
            onSubmit={async (values, { resetForm, setFieldValue }) => {
                // Check for invalid characters
                if (/[^\w\s,]/.test(values.memberIds)) {
                    handleError('Member IDs formatted incorrectly.');
                    return;
                }

                // Split & trim
                const relationshipsToAdd = [
                    ...new Set(
                        (values.memberIds?.split(/[\r\n,]/) ?? [])
                            .map((memberId) => memberId.trim())
                            .filter((memberId) => memberId),
                    ),
                ].map((memberId) => ({
                    member_id: memberId,
                    therapeutic_category: values.therapeutic_category,
                    current_supplier_npi: values.supplier?.secondaryData,
                    offer_rate: (() => {
                        if (values.offerRateType === OfferRateTypes.MARKET) {
                            return undefined;
                        }
                        if (values.offerRateType === OfferRateTypes.CONTRACT) {
                            return CONTRACT_RATE;
                        }
                        return Decimal.div(values.offerRate, 100).toNumber();
                    })(),
                    referent_price_type:
                        values.offerRateType === OfferRateTypes.OVERRIDE
                            ? values.referentPriceType
                            : undefined,
                    end_date: values.end_date
                        ? formatDate(values.end_date, DateFormats.HYPHEN_DATE)
                        : undefined,
                }));

                const currentRelationships =
                    patientPreferenceFormik.values.patientRelationships ?? [];

                const duplicateRelationships = currentRelationships.filter((currRel) =>
                    relationshipsToAdd.find((relToAdd) =>
                        isSamePatientPreference(relToAdd, currRel),
                    ),
                );

                // Display duplicate warning dialog if we aren't currently viewing it & there are duplicate member ids
                if (
                    values.duplicateRelationships.length === 0 &&
                    duplicateRelationships.length > 0
                ) {
                    await setFieldValue('duplicateRelationships', duplicateRelationships);
                    return;
                }

                patientPreferenceFormik.setFieldValue('patientRelationships', [
                    ...currentRelationships.filter(
                        (rel) => !relationshipsToAdd.find((r) => isSamePatientPreference(r, rel)),
                    ),
                    ...relationshipsToAdd,
                ]);
                resetForm();
                onClose();
            }}
        >
            {(formik) => {
                const showDuplicateWarning = formik.values.duplicateRelationships.length > 0;
                return (
                    <DialogWithForm
                        dialogTitle={
                            showDuplicateWarning
                                ? 'Duplicate Warning'
                                : `Add ${patientRelationshipTypeDisplayName}`
                        }
                        primaryButtonLabel={
                            showDuplicateWarning
                                ? `Yes, Add ${patientRelationshipTypeDisplayName}`
                                : `Add ${patientRelationshipTypeDisplayName}`
                        }
                        secondaryButtonLabel={showDuplicateWarning ? 'No, Cancel' : 'Cancel'}
                        headerIcon={showDuplicateWarning ? IconNames.WARNING_SIGN : undefined}
                        width={600}
                        isOpen={isOpen}
                        onSecondaryButtonClick={async () => {
                            if (showDuplicateWarning) {
                                await formik.setFieldValue('duplicateRelationships', []);
                            } else {
                                formik.handleReset();
                                onClose();
                            }
                        }}
                        isPrimaryButtonDisabled={
                            !(formik.dirty && formik.isValid) || formik.isSubmitting
                        }
                        dialogContent={
                            showDuplicateWarning ? (
                                <>
                                    <p className="margin__bottom--large">
                                        There are duplicate entries, and what you entered will
                                        overwrite what exists.
                                    </p>
                                    {formik.values.duplicateRelationships.map((rel) => (
                                        <div key={rel.member_id} className="margin__bottom--small">
                                            <DetailValue
                                                value={getPatientRelationshipDisplayText({
                                                    ...rel,
                                                    organization_name: suppliers?.find(
                                                        (supplier) =>
                                                            supplier.supplier_npi ===
                                                            rel.current_supplier_npi,
                                                    )?.organization_name,
                                                })}
                                            />
                                        </div>
                                    ))}
                                    <p className="margin__bottom--large">
                                        {`Are you sure you want to add the following ${patientRelationshipTypeDisplayName}?`}
                                    </p>
                                </>
                            ) : (
                                <>
                                    <TextAreaFormik
                                        id="memberIds"
                                        label="Member ID(s)"
                                        required
                                        helperText="Separate multiple Member IDs using a comma (e.g., 000000000000,111111111111) or insert one Member ID per line."
                                    />
                                    <SuggestSingleSelectFormik
                                        id="therapeutic_category"
                                        label="Therapeutic Category"
                                        isLoading={!therapeuticCategoryList}
                                        values={therapeuticCategoryList}
                                        helperText="If nothing is selected, all of the patient's treatments will match this route rule, regardless of therapeutic category."
                                    />
                                    <SuggestSingleSelectFormik
                                        id="supplier"
                                        label="Pharmacy"
                                        required
                                        values={supplierOptions}
                                        isLoading={!supplierOptions}
                                    />
                                    <RadioButtonGroupFormik
                                        name="offerRateType"
                                        label="Offer Rate"
                                        required
                                        options={Object.values(OfferRateTypes)}
                                    />
                                    {formik.values.offerRateType === OfferRateTypes.OVERRIDE && (
                                        <div className="flex__gap--medium">
                                            <SuggestSingleSelectFormik
                                                id="referentPriceType"
                                                label="Referent Price Type"
                                                required
                                                values={Object.values(ReferentPriceTypes)}
                                            />
                                            <BidInput
                                                id="offerRate"
                                                label="Offer Rate"
                                                required
                                                referentPriceType={formik.values.referentPriceType}
                                            />
                                        </div>
                                    )}
                                    <DateInputFormik id="end_date" label="End Date" />
                                </>
                            )
                        }
                    />
                );
            }}
        </Formik>
    );
};

AddPatientRelationshipDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    patientRelationshipType: PropTypes.oneOf(Object.values(PatientRelationshipType)).isRequired,
    suppliers: PropTypes.arrayOf(
        PropTypes.shape({
            organization_name: PropTypes.string,
            supplier_npi: PropTypes.string,
            network_status: PropTypes.string,
        }),
    ),
};
